import {
  type CreatedRecordData,
  type ErrorResult,
  type GetOptions,
  type IntegrationProject,
  type IntegrationRecordData,
  type JiraRecordTypeKey,
  type JiraWhiteboardIntegration,
  type RequestOptions,
  type Result,
} from '../../../../javascripts/shared-types';
import {
  type JiraIssuePicker,
  type JiraIssueBulkResponse,
  type JiraIssueCreateMetaResponse,
  type JiraIssuesResponse,
  type JiraProjectsResponse,
} from '../types';
import {
  transformAutocompleteResponse,
  transformCreateMeta,
} from './transforms';
import { type IssueUpdate, makeIssueUpdate } from './update_transforms';

interface AtlassianResource {
  id: string;
  avatarUrl: string;
  name: string;
  scopes: string[];
  url: string;
}

const API_ENDPOINT = 'https://api.atlassian.com';
const JIRA_REGEX =
  /^https:\/\/(.*)\.atlassian\.net\/browse\/([A-Z][A-Z_0-9]+-\d+)/;
const PER_PAGE = 50;

// maps jira statusCategory.key to aha status types - see
// https://docs.atlassian.com/software/jira/docs/api/7.6.1/com/atlassian/jira/issue/status/category/StatusCategory.html
const statusCategoryMap: Record<string, Aha.InternalMeaning['values']> = {
  new: 'NOT_STARTED',
  indeterminate: 'IN_PROGRESS',
  done: 'DONE',
};

const statusColorMap: Record<string, string> = {
  'blue-gray': 'rgb(103, 113, 117)',
  yellow: 'rgb(12, 102, 228)',
  green: 'rgb(31, 132, 90)',
};

const makeIssueUrl = (resource: AtlassianResource, issue: { key: string }) =>
  `${resource.url}/browse/${issue.key}`;

// converts a JIRA issue response to a normalised Aha integration record
const issueToRecordData =
  (resource: AtlassianResource) =>
  (issue: JiraIssuesResponse['issues'][0]): IntegrationRecordData => {
    const { status, priority, issuetype, assignee } = issue.fields;
    return {
      id: issue.id,
      name: issue.fields.summary,
      key: issue.key,
      recordId: issue.key,
      url: makeIssueUrl(resource, issue),
      assignee: assignee && {
        id: assignee.accountId,
        name: assignee.displayName,
        avatar: assignee.avatarUrls['48x48'],
      },
      status: {
        id: status.id,
        name: status.name,
        category: statusCategoryMap[status.statusCategory.key],
        color: statusColorMap[status.statusCategory.colorName],
      },
      type: {
        id: issuetype.id,
        name: issuetype.name,
        icon: issuetype.iconUrl,
      },
      priority: {
        id: priority.id,
        name: priority.name,
        icon: priority.iconUrl,
      },
    };
  };

// prunes and normalises a JIRA project response
const convertProject = (
  project: JiraProjectsResponse['values'][0]
): IntegrationProject => {
  return {
    id: project.id,
    name: project.name,
    key: project.key,
    recordTypes: project.issueTypes.map(issue => ({
      id: issue.id,
      name: issue.name,
      description: issue.description,
    })),
  };
};

const getHeaders = (token: string) => {
  return {
    Authorization: 'Bearer ' + token,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
};

type RequestOpts = {
  reAuth?: boolean;
  data?: unknown;
};

type MakeRequest = <R = unknown>(
  path: string,
  opts?: RequestOpts
) => Promise<R>;

const requestFactory = (): MakeRequest => {
  // GET responses keyed by url. For now, this code assumes we can always serve the same response for the same url
  const requestCache: Record<string, Promise<unknown>> = {};

  return async <R = unknown>(
    path: string,
    opts: RequestOpts = {}
  ): Promise<R> => {
    const shouldCache = !opts.data;

    if (shouldCache && path in requestCache) {
      return requestCache[path] as Promise<R>;
    }

    let resolvePromise: ((value: R) => void) | undefined;
    let rejectPromise: ((error: Error) => void) | undefined;

    if (shouldCache) {
      // populate the cache now so that concurrent requests for the same url don't double up
      requestCache[path] = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });
    }

    if (!aha) {
      throw new Error('Could not authenticate');
    }

    try {
      const { reAuth = true, data } = opts;

      const doRequest = async (forceTokenRefresh: boolean) => {
        const scopes = [
          'read:jira-work', // sync issue data to whiteboard
          'write:jira-work', // create issues from whiteboard
          'read:jira-user', // retrieve assignable users when creating issues
        ];

        const { token } = await aha.auth('atlassian', {
          useCachedRetry: !forceTokenRefresh,
          parameters: {
            scope: `offline_access ${scopes.join(' ')}`,
          },
        });
        const otherOpts = data
          ? {
              method: 'POST',
              body: JSON.stringify(data),
            }
          : {};

        const url = path.startsWith(API_ENDPOINT)
          ? path
          : `${API_ENDPOINT}${path}`;

        return fetch(url, {
          headers: getHeaders(token),
          ...otherOpts,
        });
      };

      let response = await doRequest(false);

      // if auth fails, the token might have expired, so try again with forced refresh
      if (reAuth && !response.ok && response.status === 401) {
        response = await doRequest(true);
      }

      if (!response.ok) {
        console.warn('Error accessing Jira API', response);
        throw new Error('Error connecting to Jira');
      }

      const result = (await response.json()) as R;

      if (resolvePromise) {
        // assume the promise is present if it's a cacheable request
        resolvePromise(result);
      }

      return result;
    } catch (e) {
      // reject the promise and clean it out of the cache so future requests will try again
      if (rejectPromise) {
        rejectPromise(e);
      }
      delete requestCache[path];
      throw e;
    }
  };
};

const getResourcesFactory =
  (request: MakeRequest) =>
  async (reAuth = true): Promise<AtlassianResource[]> => {
    const path = '/oauth/token/accessible-resources';
    const resources = await request<AtlassianResource[]>(path, {
      reAuth,
    });

    if (resources.length === 0) {
      // TODO better error - also maybe the user chose the wrong atlassian workspace
      // in the auth dialog? we could match the resource to the pasted url
      throw new Error('No accessible resources');
    }

    return resources;
  };

const makeJiraIntegration = (): JiraWhiteboardIntegration => {
  const request = requestFactory();

  const error = (e: Error | string): ErrorResult => ({
    status: 'error',
    error: typeof e === 'string' ? e : e.message,
  });

  const getResources = getResourcesFactory(request);

  const withResources =
    <A extends { options?: RequestOptions }, R>(
      requestFunc: (
        resources: AtlassianResource[],
        args: A
      ) => Promise<Result<R>>
    ): ((args: A) => Promise<Result<R>>) =>
    async args => {
      let resources: AtlassianResource[];
      try {
        resources = await getResources(args.options?.authenticate);
      } catch (e) {
        return error(e);
      }

      try {
        return await requestFunc(resources, args);
      } catch (e) {
        return error(e);
      }
    };

  const getCreateMeta = async (
    key: JiraRecordTypeKey,
    options?: RequestOptions
  ) => {
    const path = `/ex/jira/${key.siteId}/rest/api/3/issue/createmeta/${key.projectId}/issuetypes/${key.issueTypeId}`;
    return request<JiraIssueCreateMetaResponse>(path, {
      reAuth: options?.authenticate,
    });
  };

  const getParentOptions = async (args: {
    projectId: string;
    searchParam?: string;
  }): ReturnType<GetOptions> => {
    const result = await withResources(async ([resource]) => {
      // e.g. https://api.atlassian.com/ex/jira/eb9abe42-3e6d-42b5-bab7-c9e2c58c99c3/rest/api/3/issue/picker?currentProjectId=&showSubTaskParent=true&showSubTasks=true&currentIssueKey=null&query=
      const path = `/ex/jira/${
        resource.id
      }/rest/api/3/issue/picker?currentProjectId=${
        args.projectId
      }&showSubTasks=false&query=${args.searchParam ?? ''}`;

      // assume we get the right response back for the responseType here
      const response = await request<JiraIssuePicker>(path);
      const options = transformAutocompleteResponse({
        responseType: 'issuePicker',
        response,
      });

      return {
        status: 'success',
        data: options,
      };
    })({});

    return {
      options: result.status === 'success' ? result.data : [],
      pages: { current: 1, total: 1 },
    };
  };

  const getAutocompleteOptions = async (args: {
    autoCompleteUrl: string;
    responseType: 'user' | 'issuePicker';
    searchParam?: string;
  }): ReturnType<GetOptions> => {
    // Jira's provided urls include query= at the end, so we just append to that
    const path = args.autoCompleteUrl.replace(
      'query=',
      `query=${args.searchParam ?? ''}`
    );

    // assume we get the right response back for the responseType here
    const response = await request<any>(path);
    const options = transformAutocompleteResponse({ ...args, response });

    return {
      options,
      pages: { current: 1, total: 1 },
    };
  };

  return {
    service: 'jira',
    title: 'Jira',

    getRecordTitle(plural, includeTitle = true) {
      return `${includeTitle ? 'Jira ' : ''}work item${plural ? 's' : ''}`;
    },
    urlToRecordId(url) {
      const match = url.match(JIRA_REGEX);
      return match ? match[2] : null;
    },
    hasAuth() {
      if (!window.aha) {
        return false;
      }
      return aha.hasAuth('atlassian') ?? false;
    },

    getSites: withResources(async resources => ({
      status: 'success' as const,
      data: { sites: resources.map(r => ({ id: r.id, name: r.name })) },
    })),

    getProjects: async args => {
      const page = args.page || 1;

      const query = new URLSearchParams([
        ['expand', 'issueTypes'],
        ['maxResults', String(PER_PAGE)],
        ['startAt', String((page - 1) * PER_PAGE)],
      ]);

      if (args.searchParam) {
        query.append('query', args.searchParam);
      }

      const path = `/ex/jira/${args.siteId}/rest/api/3/project/search?${query}`;

      const result = await request<JiraProjectsResponse>(path, {
        reAuth: args.options?.authenticate,
      });

      return {
        status: 'success',
        data: {
          projects: result.values.map(convertProject),
          pages: { current: page, total: Math.ceil(result.total / PER_PAGE) },
        },
      };
    },

    getRecordFields: async args => {
      const result = await getCreateMeta(args.key, args.options);

      return {
        status: 'success',
        data: {
          fields: transformCreateMeta(result, {
            getAutocompleteOptions,
            getParentOptions: ({ searchParam }) =>
              getParentOptions({
                projectId: args.key.projectId,
                searchParam,
              }),
          }),
        },
      };
    },

    createRecords: withResources(async (resources, args) => {
      const resource = resources.find(r => r.id === args.key.siteId);
      if (!resource) {
        return error(`Invalid site id ${args.key.siteId}`);
      }

      const createMetaResult = await getCreateMeta(args.key, args.options);

      const recordEntries = Object.entries(args.records);

      const issueUpdates: IssueUpdate[] = [];
      for (const [, { summary, description }] of recordEntries) {
        const result = makeIssueUpdate(createMetaResult.fields, {
          // field data is common across records, except the description which is provided per record
          ...args.fieldData,
          description,
        });

        if (result.status === 'error') {
          return error(result.error);
        }

        issueUpdates.push({
          ...result.data,
          fields: {
            ...result.data.fields,
            summary,
            project: {
              id: args.key.projectId,
            },
            issuetype: {
              id: args.key.issueTypeId,
            },
          },
        });
      }

      const path = `/ex/jira/${args.key.siteId}/rest/api/3/issue/bulk`;
      const result = await request<JiraIssueBulkResponse>(path, {
        reAuth: args.options?.authenticate,
        data: { issueUpdates },
      });

      // this loop assumes Jira returns the issues in the same order as the summaries
      const records: Record<string, CreatedRecordData> = {};
      for (let i = 0; i < recordEntries.length; i++) {
        records[recordEntries[i][0]] = {
          ...result.issues[i],
          url: makeIssueUrl(resource, result.issues[i]),
        };
      }

      return {
        status: 'success',
        data: {
          records,
        },
      };
    }),

    getRecords: withResources(async (resources, { recordUrls, options }) => {
      const fields = ['summary', 'status', 'assignee', 'issuetype', 'priority'];

      const groupedRecords = recordUrls.reduce<Record<string, string[]>>(
        (acc, recordUrl) => {
          const match = recordUrl.match(JIRA_REGEX);
          if (match) {
            acc[match[1]] ??= [];
            acc[match[1]].push(match[2]);
          }
          return acc;
        },
        {}
      );

      const data: IntegrationRecordData[] = [];

      for (const [domain, recordIds] of Object.entries(groupedRecords)) {
        const resource = resources.find(r =>
          r.url.startsWith(`https://${domain}`)
        );
        if (!resource) {
          return { status: 'error', error: 'Resource not found' };
        }

        const query = new URLSearchParams([
          ['jql', `key in (${recordIds.join(',')})`],
          ['fields', fields.map(encodeURIComponent).join(',')],
          ['expand', 'renderedFields'],
        ]);

        const path = `/ex/jira/${resource.id}/rest/api/3/search?${query}`;
        const result = await request<JiraIssuesResponse>(path, {
          reAuth: options?.authenticate,
        });
        data.push(...result.issues.map(issueToRecordData(resource)));
      }

      return {
        status: 'success',
        data,
      };
    }),
  };
};

export default makeJiraIntegration;
